/**
 * Per-layer material-balance card: wells | properties | relative permeability chart.
 * Three equal columns; properties (and chart) height = 3/4 of section width.
 */
import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  MaterialBalanceAutoPlaceDialog,
  type AutoPlaceApplyPayload,
} from '@/components/Scenarios/MaterialBalanceAutoPlaceDialog';
import { CommitNumberField } from '@/components/Scenarios/CommitNumberField';
import { MaterialBalanceProductionPanel } from '@/components/Scenarios/MaterialBalanceProductionPanel';
import { MaterialBalancePropsAiDialog } from '@/components/Scenarios/MaterialBalancePropsAiDialog';
import { RelPermEditDialog } from '@/components/Scenarios/RelPermEditDialog';
import type { MbAutoPlaceConfig } from '@/types/materialBalance';
import type { SeedWellRow } from '@/utils/materialBalanceSeedManual';
import type { MaterialBalanceResult, MbPoint } from '@/types/materialBalance';
import {
  MB_PROP_ROWS,
  roundMbProp,
  type MbPropsForm,
} from '@/utils/materialBalanceProps';
import {
  coreyToSwGrid,
  densifyRelPermForChart,
  maskZeroKrForChart,
  relPermChartMeta,
  relPermPtsFromTable,
} from '@/utils/materialBalanceRelperm';
import type { MbOptimizeProposal } from '@/utils/materialBalanceOptimize';

export type MbWellKind = 'prod' | 'inj';

export type MbWellRow = {
  id: string;
  well_name: string;
  kind: MbWellKind;
  t0: number;
  t1: number;
  x: number | null;
  y: number | null;
  q_start_td: number | null;
};

export interface MaterialBalanceWellsSectionProps {
  scenarioId: string;
  layerId: string;
  layerName: string;
  layerColor: string;
  wells: MbWellRow[];
  selectedWellId?: string | null;
  onSelectWell?: (id: string) => void;
  onChange: (wells: MbWellRow[]) => void;
  propsForm: MbPropsForm;
  onPropsChange: (next: MbPropsForm) => void;
  onSaveProps?: () => void;
  /** Reset properties form to last saved snapshot */
  onDiscardProps?: () => void;
  propsDirty?: boolean;
  onSave?: () => void;
  /** Reset wells for this layer to last saved snapshot */
  onDiscard?: () => void;
  saving?: boolean;
  dirty?: boolean;
  readOnly?: boolean;
  propsSeedNote?: string | null;
  /** Optional saved kr table [[sw, krw, kro], ...] — plotted instead of Corey when present */
  krTable?: number[][] | null;
  /** Persist edited kr table (API + local state); optional So / E_d patch from conflict resolve */
  onSaveKrTable?: (
    table: number[][],
    propsPatch?: { so?: number; kvyt?: number },
  ) => void | Promise<void>;
  /** Last simulate() result (grids.links + series) for Production accordion */
  mbResult?: MaterialBalanceResult | null;
  /** Contour in local km for play dialog Voronoi map / auto-place */
  contourKm?: MbPoint[];
  /** Geographic origin for local-km ↔ lat/lng (basemap) */
  contourOrigin?: { lat: number; lng: number } | null;
  /** Last saved Create-with-AI settings (restored in the dialog) */
  autoPlaceConfig?: MbAutoPlaceConfig | null;
  /** Last saved wells of this layer (local km; coordinates may be missing) — Seed dialog */
  savedWells?: SeedWellRow[];
  /** Auto-place Apply → parent persists wells + inj_field + simulate */
  onAutoPlaceApply?: (result: AutoPlaceApplyPayload) => void | Promise<void>;
  autoPlaceApplying?: boolean;
  year0?: number;
  year1?: number;
  injWells?: Record<string, Record<string, number>>;
  onInjWellsChange?: (injWells: Record<string, Record<string, number>>) => void;
  onInjWellsCommit?: (injWells: Record<string, Record<string, number>>) => void | Promise<void>;
  onOptimizeAccept?: (proposal: MbOptimizeProposal) => void | Promise<void>;
  onRunMb?: () => void | Promise<void>;
  mbRunning?: boolean;
  mbRunError?: string | null;
}

/** Compact table inputs — fill cell, short vertical padding */
const denseFieldSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    fontSize: 12,
    height: 26,
  },
  '& .MuiInputBase-input': {
    py: '2px',
    px: '6px',
  },
  '& .MuiSelect-select': {
    py: '2px !important',
    px: '6px !important',
    display: 'flex',
    alignItems: 'center',
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    {
      WebkitAppearance: 'none',
      margin: 0,
    },
} as const;

const denseCellSx = {
  py: '2px',
  px: '4px',
  fontSize: 12,
  lineHeight: 1.2,
  borderBottomColor: 'divider',
} as const;

const denseHeadCellSx = {
  ...denseCellSx,
  py: '4px',
  fontSize: 11,
  fontWeight: 600,
  bgcolor: 'background.paper',
} as const;

const denseTableSx = {
  width: '100%',
  tableLayout: 'fixed' as const,
  '& .MuiTableCell-root': {
    overflow: 'hidden',
  },
};

export const MaterialBalanceWellsSection: React.FC<MaterialBalanceWellsSectionProps> = ({
  scenarioId,
  layerId,
  layerName,
  layerColor,
  wells,
  selectedWellId = null,
  onSelectWell,
  onChange,
  propsForm,
  onPropsChange,
  onSaveProps,
  onDiscardProps,
  propsDirty = false,
  onSave,
  onDiscard,
  saving = false,
  dirty = false,
  readOnly = false,
  propsSeedNote = null,
  krTable = null,
  onSaveKrTable,
  mbResult = null,
  contourKm = [],
  contourOrigin = null,
  autoPlaceConfig = null,
  savedWells = [],
  onAutoPlaceApply,
  autoPlaceApplying = false,
  year0,
  year1,
  injWells = {},
  onInjWellsChange,
  onInjWellsCommit,
  onOptimizeAccept,
  onRunMb,
  mbRunning = false,
  mbRunError = null,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [sectionWidth, setSectionWidth] = useState(0);
  const [relpermEditorOpen, setRelpermEditorOpen] = useState(false);
  const [propsAiOpen, setPropsAiOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [autoPlaceOpen, setAutoPlaceOpen] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const update = () => setSectionWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** All three columns share the same height (= 3/8 of section width) */
  const propsHeight = sectionWidth > 0 ? Math.round(sectionWidth * 0.375) : 180;
  /** Collapse columns when sidebars squeeze the content area (viewport md is not enough). */
  const configCols =
    sectionWidth > 0 && sectionWidth < 640
      ? 1
      : sectionWidth > 0 && sectionWidth < 960
        ? 2
        : 3;
  const configGridColumns =
    configCols === 1
      ? '1fr'
      : configCols === 2
        ? '1.25fr 1fr'
        : '1.25fr 1fr 0.75fr';
  const configColHeight = configCols >= 2 ? propsHeight : 'auto';

  /** Panel title + actions: stack controls under the title when the column is narrow. */
  const panelHeaderSx = {
    mb: 0.5,
    flexShrink: 0,
    width: '100%',
    minHeight: 28,
    containerType: 'inline-size',
  } as const;
  const panelHeaderInnerSx = {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0.5,
    '@container (min-width: 340px)': {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 0.75,
    },
  } as const;
  const panelActionsSx = {
    flexWrap: 'wrap',
    rowGap: 0.5,
    columnGap: 0.5,
    justifyContent: 'flex-start',
    maxWidth: '100%',
    '@container (min-width: 340px)': {
      justifyContent: 'flex-end',
      flexShrink: 0,
    },
  } as const;

  const patchWell = (index: number, key: keyof MbWellRow, val: string | number | null) => {
    onChange(
      wells.map((w, j) => {
        if (j !== index) return w;
        return { ...w, [key]: val };
      }),
    );
  };

  const add = () => {
    const n = wells.filter((w) => w.kind === 'prod').length + 1;
    const qFromProps =
      typeof propsForm.q_liq_start_td === 'number' && propsForm.q_liq_start_td > 0
        ? propsForm.q_liq_start_td
        : null;
    onChange([
      ...wells,
      {
        id: `tmp-${Date.now()}-${n}`,
        well_name: `P${n}`,
        kind: 'prod',
        t0: new Date().getFullYear(),
        t1: 2100,
        x: 0,
        y: 0,
        q_start_td: qFromProps,
      },
    ]);
  };

  const pendingDeleteWell =
    deleteIndex != null && deleteIndex >= 0 && deleteIndex < wells.length
      ? wells[deleteIndex]
      : null;

  const confirmDelete = () => {
    if (deleteIndex == null) return;
    onChange(wells.filter((_, j) => j !== deleteIndex));
    setDeleteIndex(null);
  };

  const relperm = useMemo(() => {
    const tablePts = relPermPtsFromTable(krTable);
    const mode = tablePts.length >= 2 ? ('table' as const) : ('corey' as const);
    // Corey: plateaus on Sw grid. Saved table: keep as-is (resample if needed).
    const pts =
      mode === 'table'
        ? tablePts
        : coreyToSwGrid({
            so: propsForm.so,
            kvyt: propsForm.kvyt,
          });
    return {
      mode,
      pts,
      chartData: maskZeroKrForChart(densifyRelPermForChart(pts)),
      ...relPermChartMeta(pts),
    };
  }, [krTable, propsForm.so, propsForm.kvyt]);

  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 3,
        width: '100%',
        border: 1,
        borderColor: 'divider',
        borderLeft: 4,
        borderLeftColor: layerColor,
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: layerColor,
              flexShrink: 0,
            }}
          />
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {layerName}
          </Typography>
        </Box>
      </Box>

      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
          bgcolor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 40,
            px: 1.25,
            '& .MuiAccordionSummary-content': { my: 0.75 },
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
      <Box
        ref={bodyRef}
        sx={{
          display: 'grid',
          // React to content width (sidebars), not only viewport breakpoints
          gridTemplateColumns: configGridColumns,
          gap: 1.25,
          alignItems: 'start',
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* Left: wells */}
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: configColHeight,
          }}
        >
          <Box sx={panelHeaderSx}>
            <Stack direction="row" sx={panelHeaderInnerSx}>
              <Box sx={{ minWidth: 0, pt: 0.25 }}>
                <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                  Wells
                </Typography>
                {dirty ? (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                    lineHeight={1.2}
                  >
                    unsaved
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" sx={panelActionsSx}>
                {onAutoPlaceApply && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={readOnly || saving || autoPlaceApplying || contourKm.length < 3}
                    onClick={() => setAutoPlaceOpen(true)}
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                    sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                    title={
                      contourKm.length < 3
                        ? 'Save a contour first'
                        : 'Auto-place wells on contour'
                    }
                  >
                    Seed
                  </Button>
                )}
                {onDiscard && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    disabled={readOnly || saving || !dirty}
                    onClick={() => onDiscard()}
                    sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                  >
                    Discard
                  </Button>
                )}
                {onSave && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={readOnly || saving || !dirty}
                    onClick={() => onSave()}
                    sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                  >
                    {saving ? '…' : 'Save'}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  disabled={readOnly || saving}
                  onClick={add}
                  sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: 11 }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </Button>
              </Stack>
            </Stack>
          </Box>
          {/* Reserved caption row — keeps Wells / Properties / Relperm content tops aligned */}
          <Box sx={{ mb: 0.5, flexShrink: 0, height: 16 }} aria-hidden />
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'scroll',
              overflowX: 'hidden',
              scrollbarGutter: 'stable',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&::-webkit-scrollbar': { width: 10 },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'action.hover',
                borderRadius: 1,
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'action.disabled',
                borderRadius: 1,
                border: '2px solid transparent',
                backgroundClip: 'content-box',
                '&:hover': { bgcolor: 'text.secondary' },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: (theme) =>
                `${theme.palette.action.disabled} ${theme.palette.action.hover}`,
            }}
          >
            <Table size="small" stickyHeader sx={denseTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...denseHeadCellSx, width: '16%' }}>id</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '18%' }}>type</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '12%' }}>start</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '12%' }}>end</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '12%' }}>x</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '12%' }}>y</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '10%' }}>q₀</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: 32, px: 0 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {wells.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={denseCellSx}>
                      <Typography variant="caption" color="text.secondary">
                        No wells yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  wells.map((w, i) => {
                    const selected = selectedWellId === w.id;
                    return (
                      <TableRow
                        key={w.id}
                        hover
                        selected={selected}
                        onClick={() => onSelectWell?.(w.id)}
                        sx={{ cursor: onSelectWell ? 'pointer' : 'default' }}
                      >
                        <TableCell sx={denseCellSx}>
                          <TextField
                            size="small"
                            value={w.well_name}
                            disabled={readOnly || saving}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => patchWell(i, 'well_name', e.target.value)}
                            sx={denseFieldSx}
                          />
                        </TableCell>
                        <TableCell sx={denseCellSx}>
                          <TextField
                            select
                            size="small"
                            value={w.kind}
                            disabled={readOnly || saving}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              patchWell(i, 'kind', e.target.value as MbWellKind)
                            }
                            sx={denseFieldSx}
                          >
                            <MenuItem value="prod" sx={{ fontSize: 12 }}>
                              prod
                            </MenuItem>
                            <MenuItem value="inj" sx={{ fontSize: 12 }}>
                              inj
                            </MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell sx={denseCellSx}>
                          <CommitNumberField
                            size="small"
                            value={w.t0}
                            allowEmpty={false}
                            disabled={readOnly || saving}
                            onClick={(e) => e.stopPropagation()}
                            onCommit={(n) => {
                              if (n != null) patchWell(i, 't0', Math.round(n));
                            }}
                            formatCommitted={(n) => Math.round(n)}
                            sx={denseFieldSx}
                          />
                        </TableCell>
                        <TableCell sx={denseCellSx}>
                          <CommitNumberField
                            size="small"
                            value={w.t1}
                            allowEmpty={false}
                            disabled={readOnly || saving}
                            onClick={(e) => e.stopPropagation()}
                            onCommit={(n) => {
                              if (n != null) patchWell(i, 't1', Math.round(n));
                            }}
                            formatCommitted={(n) => Math.round(n)}
                            sx={denseFieldSx}
                          />
                        </TableCell>
                        <TableCell sx={denseCellSx}>
                          <CommitNumberField
                            size="small"
                            value={w.x}
                            allowEmpty
                            disabled={readOnly || saving}
                            onClick={(e) => e.stopPropagation()}
                            onCommit={(n) => patchWell(i, 'x', n)}
                            inputProps={{ step: 0.01 }}
                            sx={denseFieldSx}
                          />
                        </TableCell>
                        <TableCell sx={denseCellSx}>
                          <CommitNumberField
                            size="small"
                            value={w.y}
                            allowEmpty
                            disabled={readOnly || saving}
                            onClick={(e) => e.stopPropagation()}
                            onCommit={(n) => patchWell(i, 'y', n)}
                            inputProps={{ step: 0.01 }}
                            sx={denseFieldSx}
                          />
                        </TableCell>
                        <TableCell sx={denseCellSx}>
                          {w.kind === 'prod' ? (
                            <CommitNumberField
                              size="small"
                              placeholder="—"
                              value={w.q_start_td}
                              allowEmpty
                              disabled={readOnly || saving}
                              onClick={(e) => e.stopPropagation()}
                              onCommit={(n) => patchWell(i, 'q_start_td', n)}
                              inputProps={{ step: 0.1 }}
                              sx={denseFieldSx}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ ...denseCellSx, px: 0, width: 32 }}>
                          <IconButton
                            size="small"
                            disabled={readOnly || saving}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteIndex(i);
                            }}
                            aria-label="Delete well"
                            sx={{ p: 0.25 }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Center: properties */}
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: configColHeight,
          }}
        >
          <Box sx={panelHeaderSx}>
            <Stack direction="row" sx={panelHeaderInnerSx}>
              <Box sx={{ minWidth: 0, pt: 0.25 }}>
                <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                  Properties
                </Typography>
                {propsDirty ? (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                    lineHeight={1.2}
                  >
                    unsaved
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" sx={panelActionsSx}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={readOnly || saving}
                  onClick={() => setPropsAiOpen(true)}
                  startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                  sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                  title="Suggest properties from a geology description"
                >
                  Create with AI
                </Button>
                {onDiscardProps && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    disabled={readOnly || saving || !propsDirty}
                    onClick={() => onDiscardProps()}
                    sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                  >
                    Discard
                  </Button>
                )}
                {onSaveProps && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={readOnly || saving || !propsDirty}
                    onClick={() => onSaveProps()}
                    sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                  >
                    {saving ? '…' : 'Save'}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ mb: 0.5, flexShrink: 0, height: 16, overflow: 'hidden' }}>
            {propsSeedNote ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block', lineHeight: '16px' }}
              >
                {propsSeedNote}
              </Typography>
            ) : null}
          </Box>
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'scroll',
              overflowX: 'hidden',
              scrollbarGutter: 'stable',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              // Always-visible scrollbar so users see there are more properties
              '&::-webkit-scrollbar': {
                width: 10,
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'action.hover',
                borderRadius: 1,
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'action.disabled',
                borderRadius: 1,
                border: '2px solid transparent',
                backgroundClip: 'content-box',
                '&:hover': {
                  bgcolor: 'text.secondary',
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: (theme) =>
                `${theme.palette.action.disabled} ${theme.palette.action.hover}`,
            }}
          >
            <Table size="small" stickyHeader sx={denseTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...denseHeadCellSx, width: '28%' }}>Param</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '48%' }}>Value</TableCell>
                  <TableCell sx={{ ...denseHeadCellSx, width: '24%' }}>Unit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MB_PROP_ROWS.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell sx={{ ...denseCellSx, whiteSpace: 'nowrap' }}>
                      <MuiTooltip
                        title={r.hint}
                        followCursor
                        enterDelay={250}
                        describeChild
                        slotProps={{
                          tooltip: {
                            sx: { maxWidth: 320, fontSize: 12, lineHeight: 1.4 },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            cursor: 'help',
                            borderBottom: '1px dotted',
                            borderColor: 'text.disabled',
                          }}
                        >
                          {r.label}
                        </Box>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell sx={denseCellSx}>
                      <CommitNumberField
                        size="small"
                        value={propsForm[r.key] === '' ? null : propsForm[r.key]}
                        allowEmpty
                        disabled={readOnly || saving}
                        formatCommitted={roundMbProp}
                        onCommit={(n) =>
                          onPropsChange({
                            ...propsForm,
                            [r.key]: n == null ? '' : n,
                          })
                        }
                        inputProps={{ step: 0.001 }}
                        sx={denseFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ ...denseCellSx, color: 'text.secondary' }}>{r.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Right: relative permeability */}
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: configColHeight,
          }}
        >
          <Box sx={panelHeaderSx}>
            <Stack direction="row" sx={panelHeaderInnerSx}>
              <Typography variant="body2" fontWeight={600} sx={{ pt: 0.25, minWidth: 0 }}>
                Relative permeability
              </Typography>
              <Stack direction="row" sx={panelActionsSx}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                  disabled={readOnly || saving}
                  onClick={() => setRelpermEditorOpen(true)}
                  sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                >
                  Edit
                </Button>
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ mb: 0.5, flexShrink: 0, height: 16, overflow: 'hidden' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block', lineHeight: '16px' }}
            >
              {relperm.mode === 'table'
                ? 'From saved kr table'
                : 'Corey from So / E_d'}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 0.5,
              bgcolor: 'background.paper',
              position: 'relative',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={relperm.chartData}
                margin={{ top: 8, right: 8, left: -8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.4} />
                <XAxis
                  dataKey="sw"
                  type="number"
                  domain={relperm.xDomain}
                  allowDataOverflow
                  tickCount={5}
                  label={{ value: 'Sw', position: 'insideBottom', offset: -2, fontSize: 10 }}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  type="number"
                  domain={relperm.yDomain}
                  allowDataOverflow
                  tickCount={5}
                  tick={{ fontSize: 10 }}
                  width={28}
                />
                <Tooltip
                  formatter={(value) =>
                    typeof value === 'number' ? value.toFixed(3) : String(value ?? '')
                  }
                  labelFormatter={(label) => `Sw = ${Number(label).toFixed(1)}`}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine
                  x={relperm.swi}
                  stroke="#94a3b8"
                  strokeDasharray="3 3"
                  ifOverflow="extendDomain"
                />
                <ReferenceLine
                  x={1 - relperm.sor}
                  stroke="#94a3b8"
                  strokeDasharray="3 3"
                  ifOverflow="extendDomain"
                />
                <Line
                  type="monotone"
                  dataKey="kro"
                  name="kro"
                  stroke="#334155"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="krw"
                  name="krw"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{
          mt: 1.25,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
          bgcolor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 40,
            px: 1.25,
            '& .MuiAccordionSummary-content': { my: 0.75 },
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Production
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
          <MaterialBalanceProductionPanel
            scenarioId={scenarioId}
            layerId={layerId}
            wells={wells}
            result={mbResult}
            contour={contourKm}
            contourOrigin={contourOrigin}
            so0={typeof propsForm.so === 'number' ? propsForm.so : 0.5}
            layerName={layerName}
            injWells={injWells}
            selectedWellId={selectedWellId}
            onSelectWell={onSelectWell}
            onRun={onRunMb}
            onInjWellsChange={onInjWellsChange}
            onInjWellsCommit={onInjWellsCommit}
            onOptimizeAccept={onOptimizeAccept}
            running={mbRunning}
            readOnly={readOnly}
            error={mbRunError}
          />
        </AccordionDetails>
      </Accordion>

      <RelPermEditDialog
        open={relpermEditorOpen}
        layerName={layerName}
        sourcePts={relperm.pts}
        so={typeof propsForm.so === 'number' ? propsForm.so : 0.5}
        kvyt={typeof propsForm.kvyt === 'number' ? propsForm.kvyt : 0.45}
        readOnly={readOnly}
        saving={saving}
        onClose={() => setRelpermEditorOpen(false)}
        onSave={async (table, propsPatch) => {
          await onSaveKrTable?.(table, propsPatch);
        }}
      />

      {onAutoPlaceApply && (
        <MaterialBalanceAutoPlaceDialog
          open={autoPlaceOpen}
          onClose={() => setAutoPlaceOpen(false)}
          scenarioId={scenarioId}
          layerId={layerId}
          layerName={layerName}
          contour={contourKm}
          origin={contourOrigin}
          initialConfig={autoPlaceConfig}
          savedWells={savedWells}
          layerQStartTd={
            typeof propsForm.q_liq_start_td === 'number' && propsForm.q_liq_start_td > 0
              ? propsForm.q_liq_start_td
              : null
          }
          year0={year0}
          year1={year1}
          qStartTd={
            typeof propsForm.q_liq_start_td === 'number' && propsForm.q_liq_start_td > 0
              ? propsForm.q_liq_start_td
              : 100
          }
          hasExistingWells={wells.length > 0}
          applying={autoPlaceApplying}
          onApply={async (result) => {
            await onAutoPlaceApply(result);
            setAutoPlaceOpen(false);
          }}
        />
      )}

      <MaterialBalancePropsAiDialog
        open={propsAiOpen}
        onClose={() => setPropsAiOpen(false)}
        scenarioId={scenarioId}
        layerId={layerId}
        layerName={layerName}
        propsForm={propsForm}
        readOnly={readOnly}
        onApply={(next) => onPropsChange(next)}
      />

      <Dialog
        open={pendingDeleteWell != null}
        onClose={() => setDeleteIndex(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete well?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDeleteWell
              ? `Remove ${pendingDeleteWell.well_name || 'this well'} from ${layerName}? Unsaved until you click Save.`
              : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIndex(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
